/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from '@mui/material';

import {
  MDTypography,
  MDInput,
  MDButton,
  CloseButton
} from '@components/index';
import { ProjectDetails } from '@DStypes/project';
import { RootState } from '@store/store';
import { useSelector } from 'react-redux';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  ADDED_PROJECT_LONG_DESCRIPTION,
  ADDED_PROJECT_SHORT_DESCRIPTION,
  CHANGED_PROJECT_NAME
} from '@constants/eventNames';

const ProjectDetailsModal = ({
  open,
  onClose,
  onUpdateProjectDetails
}: {
  open: boolean;
  onClose: () => void;
  onUpdateProjectDetails: (arg: ProjectDetails) => void;
}) => {
  const projectBoard = useSelector((state: RootState) => state?.projectBoard);
  const [errorMessage, setErrorMessage] = useState({
    project_name: '',
    project_short_desc: '',
    project_long_desc: ''
  });

  const [projectDetails, setProjectDetails] = useState({
    project_name: '',
    project_short_desc: '',
    project_long_desc: ''
  });

  const isProjectNameLimitExceeded = projectDetails.project_name?.length > 150;

  const isShortDescLimitExceeded =
    projectDetails.project_short_desc?.length > 500;

  const isLongDescLimitExceeded =
    projectDetails.project_long_desc?.length > 1000;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setProjectDetails((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleUpdateProject = () => {
    if (
      projectBoard.currentProjectData?.project_long_desc !==
      projectDetails.project_long_desc
    )
      sendAmplitudeData(ADDED_PROJECT_LONG_DESCRIPTION);

    if (
      projectBoard.currentProjectData?.project_short_desc !==
      projectDetails.project_short_desc
    )
      sendAmplitudeData(ADDED_PROJECT_SHORT_DESCRIPTION);

    if (
      projectBoard.currentProjectData?.project_name !==
      projectDetails.project_name
    )
      sendAmplitudeData(CHANGED_PROJECT_NAME);

    if (projectDetails.project_name.replace(/\s/g, '').length === 0) {
      setErrorMessage((prevState) => ({
        ...prevState,
        project_name: 'Please provide a Project Name'
      }));

      return;
    }

    if (
      !isProjectNameLimitExceeded &&
      !isShortDescLimitExceeded &&
      !isLongDescLimitExceeded
    )
      onUpdateProjectDetails(projectDetails);
  };

  useEffect(() => {
    if (projectBoard.currentProjectData)
      setProjectDetails({
        project_name: projectBoard.currentProjectData.project_name,
        project_short_desc: projectBoard.currentProjectData.project_short_desc,
        project_long_desc:
          !projectBoard.currentProjectData.project_long_desc.startsWith(
            'Junk Data:'
          )
            ? projectBoard.currentProjectData.project_long_desc
            : ''
      });
  }, [projectBoard.currentProjectData]);

  useEffect(() => {
    if (isProjectNameLimitExceeded)
      setErrorMessage((prevState) => ({
        ...prevState,
        project_name: 'Character limit 150 exceeded'
      }));
    else
      setErrorMessage((prevState) => ({
        ...prevState,
        project_name: ''
      }));

    if (isShortDescLimitExceeded)
      setErrorMessage((prevState) => ({
        ...prevState,
        project_short_desc: 'Character limit 500 exceeded'
      }));
    else
      setErrorMessage((prevState) => ({
        ...prevState,
        project_short_desc: ''
      }));

    if (isLongDescLimitExceeded)
      setErrorMessage((prevState) => ({
        ...prevState,
        project_long_desc: 'Character limit 1000 exceeded'
      }));
    else
      setErrorMessage((prevState) => ({
        ...prevState,
        project_long_desc: ''
      }));
  }, [projectDetails]);

  return (
    <Dialog
      open={open}
      maxWidth='xs'
      fullWidth
      onClose={onClose}
      sx={{
        zIndex: 9999,
        '& .MuiPaper-root': {
          position: 'relative',
          overflow: 'visible'
        }
      }}
    >
      <CloseButton onClose={() => onClose()} />
      <DialogTitle
        sx={{ pl: '20px', my: '10px' }}
        {...{ component: 'div' as any }}
      >
        <MDTypography variant='h5' color='secondary' textGradient>
          Project Name & Description
        </MDTypography>
      </DialogTitle>
      <DialogContent sx={{ px: '40px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container direction='column' spacing={1}>
              <Grid item>
                <MDTypography variant='h6' color='dark' mb={0.5}>
                  Project Name
                </MDTypography>
                <MDInput
                  fullWidth
                  name='project_name'
                  multiline
                  rows={3}
                  value={projectDetails.project_name}
                  onChange={handleChange}
                  error={errorMessage?.project_name.length !== 0}
                  helperText={errorMessage?.project_name}
                  sx={{ backgroundImage: 'none' }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container direction='column' spacing={1}>
              <Grid item>
                <MDTypography variant='h6' color='dark' mb={0.5}>
                  Project Short Description
                </MDTypography>
                <MDInput
                  name='project_short_desc'
                  fullWidth
                  multiline
                  rows={3}
                  value={projectDetails.project_short_desc}
                  onChange={handleChange}
                  error={errorMessage?.project_short_desc.length !== 0}
                  helperText={errorMessage?.project_short_desc}
                  sx={{ backgroundImage: 'none' }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container direction='column' spacing={1}>
              <Grid item>
                <MDTypography variant='h6' color='dark' mb={0.5}>
                  Project Long Description
                </MDTypography>
                <MDInput
                  name='project_long_desc'
                  fullWidth
                  multiline
                  rows={5}
                  value={projectDetails.project_long_desc}
                  onChange={handleChange}
                  error={errorMessage?.project_long_desc.length !== 0}
                  helperText={errorMessage?.project_long_desc}
                  sx={{ backgroundImage: 'none' }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ mr: 3 }}>
        <MDButton
          size='medium'
          color='primary'
          variant='gradient'
          onClick={handleUpdateProject}
        >
          Make Changes
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectDetailsModal;
